import React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Text,
  Line,
  Tooltip,
  Legend,
} from 'recharts'
import { CustomBar } from 'components/chart/barChart/CustomBar'
import { formatDateString, formatNumber, FormatNumberOptions } from 'utils/utilities'

export type StackedBarTrendLineBarConfig = {
  key: string
  color: string
  label?: string
  exclude?: boolean
  stackGroup?: string
  tooltipLabel?: string
  tooltipFormat?: FormatNumberOptions
  units?: string
}

export type StackedBarTrendLineLineConfig = {
  key: string
  color: string
  label?: string
  units?: string
  tooltipLabel?: string
  tooltipFormat?: FormatNumberOptions
  dotLabelIndexes?: 'all' | number[]
}

export type StackedBarTrendLineChartProps = {
  bars: StackedBarTrendLineBarConfig[]
  lines: StackedBarTrendLineLineConfig[]
  values: any[]
  xAxisKey: string
  title?: string
  subtitle?: string
  CustomTooltip?: React.FC<any>
  tooltipProps?: any
  savePngRef?: React.MutableRefObject<any>
  inputDateFormat?: string
  barGap?: number
}

const MORRISONS_GREEN = '#00893f'

const CustomChartTick: React.FC<any> = (props: any) => {
  return (
    <Text x={props.x} y={props.y} {...(props.tickProps ?? {})}>
      {props.tickProps?.formatter
        ? props.tickProps?.formatter(props.payload.value, { format: 'abbr' })
        : props.payload.value}
    </Text>
  )
}

export const StackedBarTrendLineChart: React.FC<StackedBarTrendLineChartProps> = (
  props: StackedBarTrendLineChartProps
) => {
  const {
    bars,
    lines,
    values,
    xAxisKey,
    title,
    subtitle,
    CustomTooltip,
    tooltipProps,
    savePngRef,
    inputDateFormat,
    barGap,
  } = props

  const LineLabel: React.FC<any> = (props: any) => {
    const { x, y, value, index, config } = props
    const dotLabelIndexes: 'all' | number[] = config.dotLabelIndexes ?? []
    if (dotLabelIndexes === 'all' || dotLabelIndexes.includes(index)) {
      return (
        <text
          x={x}
          y={y}
          dy={-10}
          dx={index === 0 ? 0 : -50}
          fill={config.color ?? 'green'}
          fontSize={18}
          fontWeight={800}
        >
          {formatNumber(value, config.tooltipFormat)}
        </text>
      )
    } else {
      return <></>
    }
  }

  const getBars = () =>
    bars.map((bar) => {
      if (bar.exclude === true) {
        return <React.Fragment key={bar.key}></React.Fragment>
      }
      return (
        <Bar
          key={bar.key}
          dataKey={bar.key}
          fill={bar.color}
          barSize={20}
          stackId={bar.stackGroup}
          shape={<CustomBar dataKey={bar.key} minThickness={1.5} />}
          yAxisId={'volumeValue'}
          name={bar.label}
          data-testid={`recharts-bar`}
        />
      )
    })

  const getLines = () =>
    lines.map((line: StackedBarTrendLineLineConfig, index: number) => {
      return (
        <Line
          key={line.key}
          type="monotone"
          yAxisId={'stockCover'}
          stroke={line.color}
          dataKey={line.key}
          dot={false}
          name={line.label}
          strokeWidth={2}
          connectNulls
          {...{ label: <LineLabel key={index} config={line} /> }}
        ></Line>
      )
    })

  return (
    <div className={`p-2 md:p-5 mt-2`} data-testid={`stacked-bar-trend-line-chart`}>
      {(title || subtitle) && (
        <div className="flex flex-col w-full">
          {title && <div className={`text-xl text-primary underline w-full text-center`}>{title}</div>}
          {subtitle && <div className={`text-lg text-primary underline w-full text-center`}>{subtitle}</div>}
        </div>
      )}
      <div className={`overflow-x-auto`}>
        <ResponsiveContainer minWidth={1600} aspect={4} debounce={1}>
          <ComposedChart
            data={values}
            margin={{ top: 40, right: 40, bottom: 20 }}
            ref={savePngRef ?? undefined}
            barGap={barGap ?? undefined}
          >
            <CartesianGrid stroke={MORRISONS_GREEN} strokeWidth={0.35} vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              interval={0}
              stroke={MORRISONS_GREEN}
              tick={
                <CustomChartTick
                  tickProps={{
                    formatter: (inputDate: string): string => formatDateString(inputDate, inputDateFormat, 'dd/MM'),
                    width: 10,
                    fill: MORRISONS_GREEN,
                    textAnchor: 'middle',
                    verticalAnchor: 'start',
                    fontSize: 16,
                  }}
                />
              }
            />
            <YAxis
              tickLine={false}
              stroke={MORRISONS_GREEN}
              tick={
                <CustomChartTick
                  tickProps={{
                    formatter: formatNumber,
                    width: 120,
                    fill: MORRISONS_GREEN,
                    textAnchor: 'end',
                    verticalAnchor: 'center',
                    fontSize: 16,
                  }}
                />
              }
              yAxisId={'volumeValue'}
            />
            <YAxis
              tickLine={false}
              stroke={MORRISONS_GREEN}
              orientation="right"
              hide={true}
              tick={
                <CustomChartTick
                  tickProps={{
                    formatter: formatNumber,
                    width: 120,
                    fill: MORRISONS_GREEN,
                    textAnchor: 'end',
                    verticalAnchor: 'center',
                    fontSize: 16,
                  }}
                />
              }
              yAxisId={'stockCover'}
            />
            {CustomTooltip ? (
              <Tooltip filterNull={false} content={<CustomTooltip bars={bars} lines={lines} {...tooltipProps} />} />
            ) : (
              <Tooltip filterNull={false} />
            )}
            {getBars()}
            {getLines()}
            <Legend
              wrapperStyle={{
                left: '0',
                right: '0',
                bottom: '20px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
              iconSize={20}
              formatter={(value) => <span className="text-primary-tint text-lg">{value}</span>}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
